import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { deleteFilterPreset, updateFilterPresets } from "api";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import errorToast from "helpers/errorToast";
import { FilterPreset } from "types/Common";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Alert from "components/molecules/Alert/Alert";
import testIds from "constants/testIds";
import EditShipmentFilter from "./EditShipmentFilter";
import EditQuoteFilter from "./EditQuoteFilter";
import EditQuickFilter from "./EditQuickFilter";
import EditInvoiceFilter from "./EditInvoiceFilter";

interface EditFilterPresetDialogProps {
  isOpen: boolean;
  close: () => void;
  selectedPreset: FilterPreset | undefined;
}

const EditFilterPresetDialog = function ({ isOpen, close, selectedPreset }: EditFilterPresetDialogProps) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [currentFilters, setFilters] = useState<Record<string, any>>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  useEffect(() => {
    updateCurrentFilters();
  }, [selectedPreset]);

  const { type } = selectedPreset ?? {};

  const updateCurrentFilters = () => {
    if (!selectedPreset) return;
    const newFilters: Record<string, any> = {};
    selectedPreset.presets.forEach((preset) => {
      newFilters[preset.label] = preset.value;
    });
    setFilters(newFilters);
  };

  const handleClose = () => {
    updateCurrentFilters();
    setShowConfirmDelete(false);
    close();
  };

  const handleClickSave = () => {
    if (!currentFilters || !selectedPreset) return;
    const presetsToSave: FilterPreset = {
      ...selectedPreset,
      presets: Object.keys(currentFilters)
        .filter(
          (key) =>
            (Array.isArray(currentFilters[key]) && currentFilters[key].length) ||
            (!Array.isArray(currentFilters[key]) && currentFilters[key] && key !== "query"),
        )
        .map((key) => ({
          label: key,
          value: currentFilters[key],
        })),
    };
    setIsLoading(true);
    updateFilterPresets([presetsToSave])
      .then(() => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userFilterPresets });
        toast(
          intl.formatMessage({
            id: "settings.presets.editSuccess",
            defaultMessage: "Successfully updated preset",
          }),
          { type: "success" },
        );
        close();
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.presets.editFail",
            defaultMessage: "Failed to update preset",
          }),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeletePreset = () => {
    if (!selectedPreset) return;
    setIsLoading(true);
    deleteFilterPreset({ data: { label: selectedPreset.label, type: selectedPreset.type } })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userFilterPresets });
        toast(
          intl.formatMessage({
            id: "settings.presets.deleteSuccess",
            defaultMessage: "Successfully deleted preset",
          }),
          { type: "success" },
        );
        close();
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.presets.deleteFail",
            defaultMessage: "Failed to delete preset",
          }),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Dialog
      size="sm"
      title={`${intl.formatMessage({
        id: "settings.presets.edit",
        defaultMessage: "Edit Preset",
      })} (${selectedPreset?.label})`}
      show={isOpen}
      onHide={handleClose}
    >
      <div style={{ maxWidth: "275px" }}>
        {type === "shipment" && <EditShipmentFilter currentFilters={currentFilters} setFilters={setFilters} />}
        {type === "quote-request" && <EditQuoteFilter currentFilters={currentFilters} setFilters={setFilters} />}
        {type === "quick-request" && <EditQuickFilter currentFilters={currentFilters} setFilters={setFilters} />}
        {type === "invoice" && <EditInvoiceFilter currentFilters={currentFilters} setFilters={setFilters} />}
      </div>

      <div className="d-flex justify-content-between mt-5">
        {!showConfirmDelete && (
          <>
            <Button onClick={handleClose} variant="outline-primary">
              <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
            </Button>
            <div className="d-flex ml-5">
              <Button disabled={isLoading} onClick={() => setShowConfirmDelete(true)} variant="danger" className="mr-2">
                <FormattedMessage id="settings.presets.delete" defaultMessage="Delete preset" />
              </Button>
              <Button disabled={isLoading} onClick={handleClickSave}>
                <FormattedMessage id="general.savePreset" defaultMessage="Save preset" />
              </Button>
            </div>
          </>
        )}

        {showConfirmDelete && (
          <div className="d-flex mt-4 align-items-center justify-content-between">
            <Alert variant="warning" className="">
              <FormattedMessage
                id="shipment-quotes.components.template.deletePresetConfirm"
                defaultMessage="Are you sure you want to delete this preset?"
              />
            </Alert>

            <span className="d-flex">
              <Button
                className="ml-3"
                variant="outline-primary"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                onClick={() => setShowConfirmDelete(false)}
              />
              <Button
                className="ml-3"
                label={intl.formatMessage({
                  id: "general.delete",
                  defaultMessage: "Delete",
                })}
                variant="danger"
                isLoading={isLoading}
                onClick={handleDeletePreset}
                data-testid={testIds.FORM_DELETE_PRESET_BUTTON}
              />
            </span>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default EditFilterPresetDialog;
